import React, { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutline';
import HandymanIcon from '@mui/icons-material/HandymanOutlined';
import CategoryIcon from '@mui/icons-material/CategoryOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import WorkIcon from '@mui/icons-material/WorkOutline';
import PaymentsIcon from '@mui/icons-material/PaymentsOutlined';
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined';
import StarIcon from '@mui/icons-material/StarOutline';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import ArticleIcon from '@mui/icons-material/ArticleOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/PersonOutline';
import { APP_NAME } from '../constants';
import { useAuth } from '../context/AuthContext';
import { ConfirmDialog } from '../components/ui';
import { initials } from '../utils';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;

const navItems = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Users', path: '/users', icon: <PeopleIcon /> },
  { label: 'Service Providers', path: '/providers', icon: <HandymanIcon /> },
  { label: 'Services & Categories', path: '/categories', icon: <CategoryIcon /> },
  { label: 'Service Requests', path: '/requests', icon: <AssignmentIcon /> },
  { label: 'Jobs', path: '/jobs', icon: <WorkIcon /> },
  { label: 'Payments', path: '/payments', icon: <PaymentsIcon /> },
  { label: 'Complaints', path: '/complaints', icon: <ReportProblemIcon /> },
  { label: 'Reviews & Ratings', path: '/reviews', icon: <StarIcon /> },
  { label: 'Notifications', path: '/notifications', icon: <NotificationsIcon /> },
  { label: 'Reports & Analytics', path: '/reports', icon: <AssessmentIcon /> },
  { label: 'CMS / Static Content', path: '/cms', icon: <ArticleIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
];

const AdminLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const drawerWidth = collapsed && !isMobile ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  const pageTitle = useMemo(() => {
    const match = navItems.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    );
    return match?.label || APP_NAME;
  }, [location.pathname]);

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          px: collapsed && !isMobile ? 1.5 : 2.5,
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            backgroundColor: 'var(--color-accent-green)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          HH
        </Box>
        {(!collapsed || isMobile) && (
          <Box sx={{ overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{APP_NAME}</Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>Admin Panel</Typography>
          </Box>
        )}
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <List sx={{ flex: 1, px: 1, py: 1.5, overflowY: 'auto' }}>
        {navItems.map((item) => {
          const active =
            location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
          return (
            <ListItemButton
              key={item.path}
              component={Link}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              selected={active}
              sx={{
                borderRadius: 1.5,
                mb: 0.5,
                minHeight: 44,
                justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
                px: collapsed && !isMobile ? 1.25 : 1.5,
                color: active ? '#fff' : 'rgba(255,255,255,0.72)',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(16, 185, 129, 0.28)',
                  '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.36)' },
                },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed && !isMobile ? 0 : 40,
                  color: 'inherit',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {(!collapsed || isMobile) && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 600 : 500 }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1 }}>
          <IconButton
            edge="start"
            onClick={() => (isMobile ? setMobileOpen(true) : setCollapsed((v) => !v))}
          >
            {isMobile ? <MenuIcon /> : collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
          <Typography variant="h3" sx={{ flex: 1 }}>
            {pageTitle}
          </Typography>
          <IconButton>
            <Badge badgeContent={3} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton onClick={(e) => setProfileAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'var(--color-accent-green)', fontSize: 13 }}>
              {initials(user?.name || 'A')}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
          >
            <Box sx={{ px: 2, py: 1.5, minWidth: 180 }}>
              <Typography fontWeight={600}>{user?.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setProfileAnchor(null);
                navigate('/settings');
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem
              onClick={() => {
                setProfileAnchor(null);
                setLogoutOpen(true);
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              backgroundColor: 'var(--color-primary-navy)',
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              backgroundColor: 'var(--color-primary-navy)',
              border: 'none',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      <ConfirmDialog
        open={logoutOpen}
        title="Confirm logout"
        message="Are you sure you want to sign out of the HomeHero admin panel?"
        confirmLabel="Logout"
        danger
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => {
          setLogoutOpen(false);
          logout();
          navigate('/login');
        }}
      />
    </Box>
  );
};

export default AdminLayout;
